import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { workspacesApi, type WorkspaceMembershipOut } from './api/workspacesApi';

interface WorkspaceContextValue {
  memberships: WorkspaceMembershipOut[];
  activeWorkspace: WorkspaceMembershipOut | null;
  activeWorkspaceId: string | null;
  isLoading: boolean;
  setActiveWorkspaceId: (id: string | null) => void;
  refetch: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const queryClient = useQueryClient();

  const { activeWorkspaceId, memberships, setMemberships, setActiveWorkspaceId } =
    useWorkspaceStore();

  const { data, isLoading } = useQuery({
    queryKey: ['workspaces'],
    queryFn: workspacesApi.list,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!data) return;
    setMemberships(data);

    // Auto-select first workspace if nothing is active or the active one disappeared
    const ids = data.map((m) => m.workspace.id);
    if (activeWorkspaceId && !ids.includes(activeWorkspaceId)) {
      setActiveWorkspaceId(data[0]?.workspace.id ?? null);
    } else if (!activeWorkspaceId && data.length > 0) {
      setActiveWorkspaceId(data[0].workspace.id);
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeWorkspace = memberships.find((m) => m.workspace.id === activeWorkspaceId) ?? null;

  const value: WorkspaceContextValue = {
    memberships,
    activeWorkspace,
    activeWorkspaceId,
    isLoading,
    setActiveWorkspaceId,
    refetch: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used inside WorkspaceProvider');
  return ctx;
}
